import { AxiosRequestConfig } from "axios";
import api from "./axiosConfig";
import { SearchFunnelSegment } from "../helpers/searchAnalytics";

export type AnalyticsPlatform = "All" | "Web" | "Native" | "iOS" | "Android";
export type AnalyticsAudience = "All" | "Anonymous" | "Identified";
export type AnalyticsGranularity = "Day" | "Week" | "Month";
export type AnalyticsBreakdown =
  | "None"
  | "Platform"
  | "Audience"
  | "AgeBand"
  | "Gender"
  | "Os"
  | "RegisteredVia";
export type AnalyticsDimension =
  | "age_band"
  | "gender"
  | "os"
  | "registered_via"
  | "platform"
  | "audience";

export interface AnalyticsBaseFilters {
  from: string; // yyyy-MM-dd, inclusive
  to: string; // yyyy-MM-dd, inclusive
  platform?: AnalyticsPlatform;
  audience?: AnalyticsAudience;
}

export interface ActivityOverviewRequest extends AnalyticsBaseFilters {
  granularity?: AnalyticsGranularity;
  breakdownBy?: AnalyticsBreakdown;
}

export interface ActivitySegment {
  key: string;
  count: number;
}

export interface ActivitySeriesBucket {
  bucketStart: string;
  segments: ActivitySegment[];
}

export interface ActivityOverviewResponse {
  activeUsers: number;
  newUsers: number;
  totalSessions: number;
  avgSessionDurationSeconds: number;
  bounceRatePercent: number;
  weeklyActiveUsers: number;
  monthlyActiveUsers: number;
  stickinessPercent: number;
  granularity: string;
  breakdownBy: string;
  series: ActivitySeriesBucket[];
}

export interface ActivityBySegmentRequest extends AnalyticsBaseFilters {
  dimension?: AnalyticsDimension;
}

export interface SegmentBucket {
  value: string;
  activeUsers: number;
}

export interface ActivityBySegmentResponse {
  dimension: string;
  buckets: SegmentBucket[];
}

export interface Pagination {
  pageNumber: number; // 1-based
  pageSize: number;
}

export interface ActivityUsersRequest extends AnalyticsBaseFilters {
  pagination: Pagination;
}

export interface ActivityUserRow {
  distinctId: string;
  personId: string | null;
  eventCount: number;
  lastSeen: string;
}

export interface ActivityUsersResponse {
  pageNumber: number;
  pageSize: number;
  total: number;
  data: ActivityUserRow[];
}

export interface UserJourneyRequest {
  distinctId: string;
  from: string;
  to: string;
  pagination: Pagination;
}

export interface JourneyEvent {
  uuid: string;
  event: string;
  timestamp: string;
  sessionId: string;
  propertiesJson: string;
}

export interface UserJourneyResponse {
  distinctId: string;
  events: JourneyEvent[];
}

export const getActivityOverview = async (
  request: ActivityOverviewRequest
): Promise<ActivityOverviewResponse> => {
  try {
    const response = await api.post("/analytics-admin/activity/overview", request);
    return response.data;
  } catch (error) {
    console.error("Getting activity overview error:", error);
    throw error;
  }
};

export const getActivityBySegment = async (
  request: ActivityBySegmentRequest
): Promise<ActivityBySegmentResponse> => {
  try {
    const response = await api.post("/analytics-admin/activity/by-segment", request);
    return response.data;
  } catch (error) {
    console.error("Getting activity by-segment error:", error);
    throw error;
  }
};

export interface ActivityDashboardRequest extends AnalyticsBaseFilters {
  granularity?: AnalyticsGranularity;
}

// Single call that returns everything the Overview tab needs, replacing the
// per-breakdown overview + by-segment requests.
export interface ActivityDashboardResponse {
  activeUsers: number;
  totalSessions: number;
  avgSessionDurationSeconds: number;
  granularity: string;
  // Optional because the deployed swagger's ActivityDashboardViewModel does not
  // declare them: only activeUsers, totalSessions, avgSessionDurationSeconds,
  // granularity, series and segments are guaranteed. Reading one of these as if
  // it were always a number is what blanked /admin — `response.data` is `any`,
  // so the compiler cannot catch the claim being wrong.
  newUsers?: number;
  bounceRatePercent?: number;
  weeklyActiveUsers?: number;
  monthlyActiveUsers?: number;
  stickinessPercent?: number;
  // Keyed by breakdown: "none" plus one per dimension (platform, audience,
  // gender, os, registered_via, age_band). Every key is always present;
  // series.none carries the headline line as a single segment (key "all").
  series: Record<string, ActivitySeriesBucket[]>;
  // Keyed by dimension (the six above, no "none"); buckets ordered desc.
  segments: Record<string, SegmentBucket[]>;
}

export const adaptActivityDashboard = (data: any): ActivityDashboardResponse => ({
  ...data,
  series: data.series ?? {},
  segments: data.segments ?? {},
});

export const getActivityDashboard = async (
  request: ActivityDashboardRequest,
  // Callers that must not hang — the dashboard, where this is one widget among
  // several — pass a timeout. The shared axios instance sets none, so without it
  // a request waits for whatever cuts the connection upstream.
  config?: AxiosRequestConfig
): Promise<ActivityDashboardResponse> => {
  try {
    const response = await api.post("/analytics-admin/activity/dashboard", request, config);
    return adaptActivityDashboard(response.data);
  } catch (error) {
    console.error("Getting activity dashboard error:", error);
    throw error;
  }
};

export const getActivityUsers = async (
  request: ActivityUsersRequest
): Promise<ActivityUsersResponse> => {
  try {
    const response = await api.post("/analytics-admin/activity/users", request);
    return response.data;
  } catch (error) {
    console.error("Getting activity users error:", error);
    throw error;
  }
};

export const getUserJourney = async (
  request: UserJourneyRequest
): Promise<UserJourneyResponse> => {
  try {
    const response = await api.post("/analytics-admin/activity/user-journey", request);
    return response.data;
  } catch (error) {
    console.error("Getting user journey error:", error);
    throw error;
  }
};

export type SearchPreset = "Last3Days" | "Last7Days" | "Last30Days" | "Custom";
export type SearchBreakdown = "None" | "Gender" | "AgeBand" | "TypeaheadOrigin";
export type SearchQuerySort =
  | "Volume"
  | "OpenRate"
  | "OutboundRate"
  | "ZeroConversion"
  | "LowestOpenRate"
  | "ZeroResults";

export interface SearchBaseFilters {
  preset: SearchPreset;
  from?: string; // required when preset === "Custom"
  to?: string; // required when preset === "Custom", >= from
  platform?: AnalyticsPlatform;
  audience?: AnalyticsAudience;
}

export interface SearchFunnelOverviewRequest extends SearchBaseFilters {
  breakdown?: SearchBreakdown;
}

export interface SearchFunnelOverviewResponse {
  breakdown: string;
  segments: SearchFunnelSegment[];
}

export interface SearchQueryFunnelRequest extends SearchBaseFilters {
  topN?: number; // default 50, 1..500
  minSearches?: number; // default 3, >= 0
  sort?: SearchQuerySort; // default "Volume"
}

export interface SearchQueryRow {
  query: string;
  displayQuery: string | null;
  searches: number;
  uniqueSearchers: number;
  opens: number;
  outbounds: number;
  searchToOpenRate: number;
  openToOutboundRate: number;
  searchToOutboundRate: number;
  zeroResultRate: number;
}

export interface SearchQueryFunnelResponse {
  items: SearchQueryRow[];
}

export const adaptSearchFunnelOverview = (
  data: any
): SearchFunnelOverviewResponse => ({ ...data, segments: data.segments ?? [] });

export const getSearchFunnelOverview = async (
  request: SearchFunnelOverviewRequest
): Promise<SearchFunnelOverviewResponse> => {
  try {
    const response = await api.post(
      "/analytics-admin/search/funnel-overview",
      request
    );
    return adaptSearchFunnelOverview(response.data);
  } catch (error) {
    console.error("Getting search funnel overview error:", error);
    throw error;
  }
};

export const adaptSearchQueryFunnel = (
  data: any
): SearchQueryFunnelResponse => ({ ...data, items: data.items ?? [] });

export const getSearchQueryFunnel = async (
  request: SearchQueryFunnelRequest
): Promise<SearchQueryFunnelResponse> => {
  try {
    const response = await api.post(
      "/analytics-admin/search/query-funnel",
      request
    );
    return adaptSearchQueryFunnel(response.data);
  } catch (error) {
    console.error("Getting search query funnel error:", error);
    throw error;
  }
};

export interface SearchFunnelSeriesRequest extends SearchBaseFilters {
  breakdown?: SearchBreakdown;
  granularity?: AnalyticsGranularity;
}

export interface SearchSeriesPoint {
  bucketStart: string;
  searches: number;
  searchesWithOpen: number;
  opens: number;
  outbounds: number;
  searchesWithOutbound: number;
  uniqueSearchers: number;
}

export interface SearchSegmentSeries {
  segment: string; // "all" when breakdown === "None"
  points: SearchSeriesPoint[];
}

export interface SearchFunnelSeriesResponse {
  breakdown: string;
  granularity: string;
  series: SearchSegmentSeries[];
}

export const adaptSearchFunnelSeries = (
  data: any
): SearchFunnelSeriesResponse => ({ ...data, series: data.series ?? [] });

export const getSearchFunnelSeries = async (
  request: SearchFunnelSeriesRequest
): Promise<SearchFunnelSeriesResponse> => {
  try {
    const response = await api.post(
      "/analytics-admin/search/funnel-series",
      request
    );
    return adaptSearchFunnelSeries(response.data);
  } catch (error) {
    console.error("Getting search funnel series error:", error);
    throw error;
  }
};

export interface SearchTrendingRequest extends SearchBaseFilters {
  granularity?: AnalyticsGranularity;
  topN?: number;
}

export interface TrendingItem {
  rank: number;
  query: string;
  displayQuery: string | null;
  searches: number;
  prevSearches: number;
  delta: number;
  deltaPct: number | null;
  isNew: boolean;
}

export interface TrendingBucket {
  bucketStart: string;
  items: TrendingItem[];
}

export interface SearchTrendingResponse {
  granularity: string;
  buckets: TrendingBucket[];
}

export const adaptSearchTrending = (data: any): SearchTrendingResponse => ({
  ...data,
  buckets: (data.buckets ?? []).map(
    (b: { bucketStart: string; items?: TrendingItem[] | null }) => ({
      ...b,
      items: b.items ?? [],
    })
  ),
});

export const getSearchTrending = async (
  request: SearchTrendingRequest
): Promise<SearchTrendingResponse> => {
  try {
    const response = await api.post(
      "/analytics-admin/search/trending",
      request
    );
    return adaptSearchTrending(response.data);
  } catch (error) {
    console.error("Getting search trending error:", error);
    throw error;
  }
};

// ----- Clicks & Conversion analytics -----

export type ClicksBreakdown =
  | "None"
  | "Source"
  | "Section"
  | "Category"
  | "OfferType"
  | "Gender"
  | "AgeBand";

export type ClicksBusinessSort =
  | "Volume"
  | "OutboundRate"
  | "ConversionRate"
  | "ZeroConversion";

export type ClicksTrendingMetric = "Opens" | "Outbounds" | "Conversions";

export interface ClicksBaseFilters {
  preset: SearchPreset;
  from?: string; // required when preset === "Custom"
  to?: string; // required when preset === "Custom", >= from
  platform?: AnalyticsPlatform;
  audience?: AnalyticsAudience;
  conversionWindowDays?: number; // 1..30, default 4
}

export interface ClicksMetrics {
  opens: number;
  outbounds: number;
  opensWithOutbound: number;
  uniqueOpeners: number;
  openedPairs: number;
  convertedPairs: number;
  openToOutboundRate: number;
  openToConversionRate: number;
}

export interface ClicksOverviewSegment extends ClicksMetrics {
  name: string;
}

export interface ClicksOverviewRequest extends ClicksBaseFilters {
  breakdown?: ClicksBreakdown;
}

export interface ClicksOverviewResponse {
  breakdown: string;
  conversionWindowDays: number;
  segments: ClicksOverviewSegment[];
}

export interface ClicksBusinessItem extends ClicksMetrics {
  businessId: string;
  displayName: string | null;
}

export interface ClicksBusinessFunnelRequest extends ClicksBaseFilters {
  topN?: number; // default 50, 1..500
  minOpens?: number; // default 3, >= 1
  sort?: ClicksBusinessSort; // default "Volume"
}

export interface ClicksBusinessFunnelResponse {
  conversionWindowDays: number;
  items: ClicksBusinessItem[];
}

export interface ClicksSeriesPoint {
  bucketStart: string;
  opens: number;
  outbounds: number;
  opensWithOutbound: number;
  uniqueOpeners: number;
  openedPairs: number;
  convertedPairs: number;
}

export interface ClicksSegmentSeries {
  segment: string; // "all" when breakdown === "None"
  points: ClicksSeriesPoint[];
}

export interface ClicksSeriesRequest extends ClicksBaseFilters {
  breakdown?: ClicksBreakdown;
  granularity?: AnalyticsGranularity;
}

export interface ClicksSeriesResponse {
  breakdown: string;
  granularity: string;
  conversionWindowDays: number;
  series: ClicksSegmentSeries[];
}

export interface ClicksTrendingRequest extends ClicksBaseFilters {
  granularity?: AnalyticsGranularity;
  topN?: number; // default 10, 1..100
  metric?: ClicksTrendingMetric; // default "Opens"
}

// buckets are pre-adapted into the shared TrendingItem shape so the existing
// trending/ components and helpers can be reused unchanged.
export interface ClicksTrendingResponse {
  granularity: string;
  metric: string;
  buckets: TrendingBucket[];
}

export const adaptClicksOverview = (data: any): ClicksOverviewResponse => ({
  ...data,
  segments: data.segments ?? [],
});

export const getClicksOverview = async (
  request: ClicksOverviewRequest
): Promise<ClicksOverviewResponse> => {
  try {
    const response = await api.post("/analytics-admin/clicks/overview", request);
    return adaptClicksOverview(response.data);
  } catch (error) {
    console.error("Getting clicks overview error:", error);
    throw error;
  }
};

export const adaptClicksBusinessFunnel = (
  data: any
): ClicksBusinessFunnelResponse => ({ ...data, items: data.items ?? [] });

export const getClicksBusinessFunnel = async (
  request: ClicksBusinessFunnelRequest
): Promise<ClicksBusinessFunnelResponse> => {
  try {
    const response = await api.post(
      "/analytics-admin/clicks/business-funnel",
      request
    );
    return adaptClicksBusinessFunnel(response.data);
  } catch (error) {
    console.error("Getting clicks business funnel error:", error);
    throw error;
  }
};

export const adaptClicksSeries = (data: any): ClicksSeriesResponse => ({
  ...data,
  series: data.series ?? [],
});

export const getClicksSeries = async (
  request: ClicksSeriesRequest
): Promise<ClicksSeriesResponse> => {
  try {
    const response = await api.post("/analytics-admin/clicks/series", request);
    return adaptClicksSeries(response.data);
  } catch (error) {
    console.error("Getting clicks series error:", error);
    throw error;
  }
};

interface RawClicksTrendingItem {
  rank: number;
  businessId: string;
  displayName: string | null;
  value: number;
  prevValue: number;
  delta: number;
  deltaPct: number | null;
  isNew: boolean;
}

const adaptClicksTrendingItem = (it: RawClicksTrendingItem): TrendingItem => ({
  rank: it.rank,
  query: it.businessId,
  displayQuery: it.displayName,
  searches: it.value,
  prevSearches: it.prevValue,
  delta: it.delta,
  deltaPct: it.deltaPct,
  isNew: it.isNew,
});

// Not just a null guard: the payload is reshaped onto the search-trending vocabulary
// (businessId -> query, value -> searches) so both tabs can share the same trending
// components. Anything reading this endpoint must apply it, or the rows arrive
// looking empty with nothing to show for it.
export const adaptClicksTrending = (data: any): ClicksTrendingResponse => ({
  granularity: data.granularity,
  metric: data.metric,
  buckets: (
    (data.buckets ?? []) as {
      bucketStart: string;
      items?: RawClicksTrendingItem[] | null;
    }[]
  ).map((b) => ({
    bucketStart: b.bucketStart,
    items: (b.items ?? []).map(adaptClicksTrendingItem),
  })),
});

export const getClicksTrending = async (
  request: ClicksTrendingRequest
): Promise<ClicksTrendingResponse> => {
  try {
    const response = await api.post("/analytics-admin/clicks/trending", request);
    return adaptClicksTrending(response.data);
  } catch (error) {
    console.error("Getting clicks trending error:", error);
    throw error;
  }
};
